import { motion } from "framer-motion";
import {
  Crosshair,
  Layers3,
  BadgeCheck,
  UserRoundCheck,
  Cog,
  LineChart,
} from "lucide-react";

const REASONS = [
  {
    n: "01",
    icon: Crosshair,
    label: "Business Positioning",
    body: "We architect a point of view sharp enough to command premium pricing and repel the wrong-fit clients.",
    tags: ["Positioning", "Offer", "Pricing"],
  },
  {
    n: "02",
    icon: Layers3,
    label: "Content Engineering",
    body: "Every reel, hook and script is engineered against a repeatable framework — not vibes, not guesswork.",
    tags: ["Hooks", "Scripts", "Systems"],
  },
  {
    n: "03",
    icon: BadgeCheck,
    label: "Personal Brand Authority",
    body: "We turn quiet experts into the visible category leaders their industry cites, references and pays a premium for.",
    tags: ["Authority", "Distribution", "PR"],
  },
  {
    n: "04",
    icon: UserRoundCheck,
    label: "Client Acquisition",
    body: "Structured funnels, qualified leads, high-intent conversations. Sales becomes a system, not luck.",
    tags: ["Funnels", "Leads", "Sales"],
  },
  {
    n: "05",
    icon: Cog,
    label: "Automation Systems",
    body: "DMs, follow-ups, appointments and nurtures — automated so your calendar fills while you focus on delivery.",
    tags: ["DMs", "Follow-ups", "CRM"],
  },
  {
    n: "06",
    icon: LineChart,
    label: "Revenue Scaling",
    body: "Webinar launches, one-to-many offers and infrastructure that turn audience into a compounding P&amp;L.",
    tags: ["Webinars", "Launches", "P&L"],
  },
];

export default function WhyVIRALX() {
  return (
    <section
      data-testid="why-section"
      className="relative py-16 md:py-24 border-t border-[#e5e2d8]"
    >
      <div className="mx-auto max-w-[1200px] px-5 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12 md:mb-16 items-end">
          <div className="lg:col-span-8">
            <span className="eyebrow" data-testid="why-eyebrow">
              <span className="eyebrow-dot" />
              Why VIRALX
            </span>
            <h2 className="mt-6 font-semibold tracking-tight text-3xl md:text-5xl lg:text-6xl leading-[1.05] max-w-3xl">
              <span className="text-gradient">Not a content agency.</span>{" "}
              <span className="text-gradient-dim">A business growth partner.</span>
            </h2>
          </div>
          <div className="lg:col-span-4">
            <p className="text-[#0a0a0a]/60 text-[15px] leading-relaxed">
              Six disciplines, one integrated engine — built specifically for
              experts who want business outcomes, not vanity metrics.
            </p>
          </div>
        </div>

        {/* Staggered rail (matches Process pattern) */}
        <div className="relative">
          <div
            aria-hidden
            className="hidden md:block absolute left-1/2 top-4 bottom-4 w-px -translate-x-1/2 bg-gradient-to-b from-white/[0.02] via-white/[0.08] to-white/[0.02]"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-x-16 md:gap-y-8">
            {REASONS.map((r, i) => {
              const Icon = r.icon;
              return (
                <motion.article
                  key={r.n}
                  data-testid={`why-card-${i}`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.7, delay: (i % 2) * 0.08 }}
                  className={`relative card-raised card-hover p-8 md:p-10 overflow-hidden ${
                    i % 2 === 1 ? "md:mt-24" : ""
                  }`}
                >
                  {/* Huge ghost number */}
                  <div className="absolute -top-6 -right-2 text-[150px] md:text-[190px] font-semibold leading-none text-[#0a0a0a]/[0.035] select-none pointer-events-none tracking-tighter">
                    {r.n}
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 text-[#0a0a0a]/50 text-[11.5px] tracking-[0.22em] uppercase">
                      <span className="w-6 h-px bg-white/25" />
                      Pillar {r.n}
                    </div>
                    <span className="icon-square" style={{ width: 40, height: 40, borderRadius: 10 }}>
                      <Icon className="w-4 h-4 text-[#0a0a0a]/85" strokeWidth={1.7} />
                    </span>
                  </div>

                  <h3 className="mt-5 text-[#0a0a0a] text-2xl md:text-[30px] font-medium tracking-tight leading-tight">
                    {r.label}
                  </h3>
                  <p
                    className="mt-3 text-[#0a0a0a]/60 text-[14px] leading-relaxed max-w-md"
                    dangerouslySetInnerHTML={{ __html: r.body }}
                  />

                  <div className="mt-6 flex flex-wrap gap-2">
                    {r.tags.map((t) => (
                      <span
                        key={t}
                        className="text-[11.5px] px-2.5 py-1 rounded-full border border-black/10 bg-white text-[#0a0a0a]/55"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </motion.article>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
